/**
 * Configuración → Conexiones de la clínica (dirección, 29/9/2026).
 *
 * La tablet entra con una contraseña corta (menos de 8 caracteres), y
 * dirección decidió que esa corta SOLO valga desde la conexión de la clínica.
 * Aquí se dice cuáles son: IPs sueltas o redes (203.0.113.0/24), IPv4 o IPv6.
 * Con una contraseña de 8 o más, la tablet entra desde cualquier sitio, como
 * el resto del equipo.
 *
 * «Tu conexión ahora» es la IP con la que el SERVIDOR ve esta pantalla (la
 * misma que mira el login), no la que diga el navegador: si se abre desde el
 * mostrador, «Añadir la IP de esta conexión» añade la de la clínica.
 *
 * Solo admin; cada cambio se guarda al momento y deja rastro con el antes y el
 * después. Por el conector no se lee ni se cambia (settings_router).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { api, mensajeError } from '../../lib/api';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash2, Wifi, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const RUTA = '/api/settings/ips-clinica';

export default function IpsClinicaPanel() {
  const [datos, setDatos] = useState(null);     // { ips, tu_ip, tu_ip_dentro, tablets }
  const [errorCarga, setErrorCarga] = useState('');
  const [nueva, setNueva] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get(RUTA);
      setDatos(r.data || {}); setErrorCarga('');
    } catch (e) {
      setErrorCarga(mensajeError(e, 'No se han podido leer las conexiones de la clínica.'));
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async (cuerpo, hecho) => {
    setGuardando(true);
    try {
      const r = await api.put(RUTA, cuerpo);
      setDatos(r.data || {});
      toast.success(hecho);
      return true;
    } catch (e) {
      toast.error(mensajeError(e, 'No se han podido guardar las conexiones de la clínica.'));
      return false;
    } finally { setGuardando(false); }
  };

  const ips = Array.isArray(datos?.ips) ? datos.ips : [];

  const anadir = async () => {
    const texto = nueva.trim();
    if (!texto) { toast.error('Escribe una IP o una red (por ejemplo, 203.0.113.0/24)'); return; }
    if (await guardar({ ips: [...ips, texto] }, 'Conexión añadida')) setNueva('');
  };
  // La IP la pone el servidor (la que ve él), no la que pinta la pantalla.
  const anadirEsta = () => guardar({ ips, anadir_esta_conexion: true }, 'Añadida la IP de esta conexión');
  const quitar = (ip) => guardar({ ips: ips.filter((x) => x !== ip) }, 'Conexión quitada');

  return (
    <SectionCard title="Conexiones de la clínica (tablet)">
      <div className="space-y-3 text-sm" data-testid="ips-clinica-panel">
        <p className="text-muted-foreground">
          La contraseña corta de la tablet (menos de 8 caracteres) solo vale desde estas
          conexiones. Con una de 8 o más, la tablet entra desde cualquier sitio, como el resto.
        </p>
        {errorCarga ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800" data-testid="ips-clinica-error">
            <p>{errorCarga}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={cargar}>Reintentar</Button>
          </div>
        ) : !datos ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2" data-testid="ips-clinica-tu-conexion">
              <Wifi className="w-4 h-4 text-[#2E6E5E]" aria-hidden="true" />
              <span>Tu conexión ahora: <code className="bg-muted/40 px-1 rounded">{datos.tu_ip || 'desconocida'}</code></span>
              {datos.tu_ip && (
                <span className={datos.tu_ip_dentro ? 'text-green-700' : 'text-muted-foreground'}>
                  {datos.tu_ip_dentro ? '· ya es de la clínica' : '· no está en la lista'}
                </span>
              )}
              <Button
                type="button" variant="outline" size="sm" className="ml-auto"
                disabled={guardando || !datos.tu_ip || datos.tu_ip_dentro}
                onClick={anadirEsta}
                data-testid="ips-clinica-anadir-esta"
              >
                <Plus className="w-4 h-4 mr-1" /> Añadir la IP de esta conexión
              </Button>
            </div>

            {ips.length === 0 && Number(datos.tablets) > 0 && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="ips-clinica-aviso-tablets">
                No hay ninguna conexión de la clínica: con una contraseña de menos de 8 caracteres,
                la tablet no podrá entrar.
              </p>
            )}

            {ips.length > 0 && (
              <ul className="divide-y divide-border rounded-md border border-border" data-testid="ips-clinica-lista">
                {ips.map((ip) => (
                  <li key={ip} className="flex items-center justify-between px-3 py-1.5">
                    <code className="text-sm">{ip}</code>
                    <Button
                      type="button" variant="ghost" size="sm" className="text-destructive"
                      disabled={guardando} onClick={() => quitar(ip)}
                      title={`Quitar ${ip}`} data-testid={`ips-clinica-quitar-${ip}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div>
              <Label htmlFor="ips-clinica-nueva">Añadir una IP o una red</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="ips-clinica-nueva" value={nueva} onChange={(e) => setNueva(e.target.value)}
                  placeholder="203.0.113.7 o 203.0.113.0/24"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); anadir(); } }}
                  data-testid="ips-clinica-nueva"
                />
                <Button type="button" variant="outline" disabled={guardando} onClick={anadir} data-testid="ips-clinica-anadir">
                  Añadir
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}
